<template>
  <div :class="$style.root">
    <div :class="$style.main">
      <div :class="$style.header">
        <h3>店舗在庫状況</h3>
      </div>

      <div :class="$style.body">
        <div :class="$style.main_image">
          <AtomsConsumerImage :src="selectedSku?.main_image ?? ''" />
        </div>
        <div :class="$style.content">
          <div :class="$style.title_with_price">
            <div>
              <h4 :class="$style.title">{{ product?.name }}</h4>
              <p>{{ product?.material }}</p>
            </div>

            <MoleculesConsumerCartPrice :sku="selectedSku"></MoleculesConsumerCartPrice>
          </div>

          <div :class="$style.color_with_size">
            <PagePartsCartPartsSelectColor
              v-if="selectedSku && typeof selectedSku.color === 'string'"
              :selected-sku="selectedSku"
              :skus="product?.skus"
              :click-event="changeSelectedSku"
              :color-type="product?.color_type"
              :selected-color-label="selectedSkuColorLabel"
            />

            <div :class="$style.size_container">
              <div :class="$style.label">
                <div :class="$style.left">
                  <p>{{ selectedSkuSizeLabel }}</p>
                  <p>{{ selectedSku?.size }}</p>
                </div>

                <AtomsConsumerLink
                  v-if="product?.size_chart.pc_img_url || product?.size_chart.sp_img_url"
                  :class="$style.link"
                  @click="openSizeChart = true"
                  >サイズチャートを見る</AtomsConsumerLink
                >
              </div>
              <PagePartsCartPartsSelectSize
                v-if="selectedSku"
                :selected-sku="selectedSku"
                :selected-size="selectedSku?.size"
                :skus="product?.skus"
                :click-event="changeSelectedSku"
                ignore-stock-check
              />
            </div>
          </div>

          <div :class="$style.description">
            <p>※ 在庫状況は1時間ごとに更新されます。</p>
            <p>※ 在庫状況は変動するため、詳細は店舗までお問い合わせください。</p>
            <p>※ TENTIAL OFFICIAL STOREでのお取り扱い商品になります。</p>
          </div>
        </div>
      </div>
    </div>

    <div :class="$style.shop_info">
      <h4 :class="$style.title">お取り扱いのある店舗</h4>
      <template v-for="store in storesDetail" :key="store.prefecture">
        <MoleculesConsumerAccordion
          v-if="store.filteredStocks.length"
          :icon="'plus-minus'"
          is-open-by-default
          :class="$style.accordion"
        >
          <template #title>{{ store.prefecture }}</template>
          <template #content>
            <div :class="$style.content">
              <template v-if="isSetItem">
                <div v-for="stock in store.filteredSetStocks" :key="stock.store?.document_id" :class="$style.row">
                  <div :class="$style.store">
                    <AtomsConsumerLink :url="`/shop/detail/${stock.store?.document_id}`" blank :class="$style.link">
                      {{ stock.store?.store_name }}
                    </AtomsConsumerLink>
                  </div>
                  <p v-if="stock.topsStock"
                    >トップス:
                    <span :class="$style[displayStockMark(stock.topsStock).key]">
                      {{ displayStockMark(stock.topsStock).label }}
                    </span>
                  </p>
                  <p v-if="stock.bottomsStock">
                    ボトムス:
                    <span :class="$style[displayStockMark(stock.bottomsStock).key]">
                      {{ displayStockMark(stock.bottomsStock).label }}
                    </span>
                  </p>
                </div>
              </template>
              <template v-else>
                <div v-for="stock in store.filteredStocks" :key="stock.store?.document_id" :class="$style.row">
                  <div :class="$style.store">
                    <AtomsConsumerLink :url="`/shop/detail/${stock.store?.document_id}`" blank :class="$style.link">
                      {{ stock.store?.store_name }}
                    </AtomsConsumerLink>
                  </div>
                  <p :class="$style[displayStockMark(stock).key]">
                    {{ displayStockMark(stock).label }}
                  </p>
                </div>
              </template>
            </div>
          </template>
        </MoleculesConsumerAccordion>
      </template>
      <div v-if="storesDetail && storesDetail.every((store) => store.filteredStocks.length === 0)">
        店舗在庫はありません
      </div>
    </div>

    <MoleculesConsumerModal v-model="openSizeChart">
      <AtomsConsumerImage
        :src="(minScreenMd ? product?.size_chart.pc_img_url : product?.size_chart.sp_img_url) ?? ''"
      />
    </MoleculesConsumerModal>
  </div>
</template>

<script setup lang="ts">
import { StoreStockAndArea } from "~/const/shop"
import { SkuFragmentFragment } from "~/types/type.generated"
import { PagePartsProductType } from "~/types/product"
import { EventHandler } from "~/types/events"
import type { CartPartsDisplayStockMarkType } from "@/types/cart"
import type { ApiDataStockResponse } from "~/server/api/data/stock/index.get"

const { width: windowWidth } = useWindowSize()
const minScreenMd = computed(() => windowWidth.value >= 768)

type Props = {
  selectedSku: SkuFragmentFragment | null
  storesDetail: StoreStockAndArea[] | null
  product: PagePartsProductType | null
  isSetItem: boolean
}

type Emits = {
  (e: "enter", el: Element): void
  (e: "leave", el: Element): void
  (e: "afterEnter", el: Element): void
  (e: "afterLeave", el: Element): void
  (e: "update:selectedSku", sku: SkuFragmentFragment): void
}

const emit = defineEmits<Emits>()

// TODO: イベント実装
// const enter = (el: Element) => {
//   emit("enter", el)
// }
// const leave = (el: Element) => {
//   emit("leave", el)
// }
// const afterEnter = (el: Element) => {
//   emit("afterEnter", el)
// }
// const afterLeave = (el: Element) => {
//   emit("afterLeave", el)
// }

const props = withDefaults(defineProps<Props>(), {
  selectedSku: null,
  storesDetail: null,
  product: null,
  isSetItem: false,
})

const openSizeChart = ref(false)

const selectedSkuSizeLabel = computed(() => {
  return props.selectedSku?.size_label ? props.selectedSku?.size_label : "サイズ"
})
const selectedSkuColorLabel = computed(() => {
  return props.selectedSku?.color_label ? props.selectedSku?.color_label : "カラー"
})

const displayStockMark = (
  stock: ApiDataStockResponse[number],
): {
  key: CartPartsDisplayStockMarkType
  label: string
} => {
  if (stock.stock_quantity > stock.stock_limit) {
    return { key: "normal", label: stock.normal_stock_text || "在庫あり" }
  } else if (stock.stock_quantity > 0 && stock.stock_quantity <= stock.stock_limit) {
    return { key: "few", label: stock.few_stock_text || "残りわずか" }
  }
  return { key: "none", label: stock.none_stock_text || "在庫なし" }
}

const changeSelectedSku: EventHandler = (sku: SkuFragmentFragment) => {
  if (props.selectedSku?.document_id === sku.document_id) return
  emit("update:selectedSku", sku)
}
</script>

<style scoped module lang="scss">
.root {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;

  @include md {
    margin-top: 0;
    max-width: $max-width-md;
  }

  .main {
    .header {
      font-size: $font-size-18;
      color: $primary;
    }

    .body {
      @include md {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        gap: 2.5rem;
      }

      .main_image {
        width: 100%;
        aspect-ratio: 323 / 430;
        @include md {
          width: 25%;
        }
        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      .content {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-top: 1.25rem;

        @include md {
          margin-top: 0;
        }

        .title_with_price {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;

          .title {
            font-size: $font-size-15;
            font-weight: 600;
          }

          .price_container {
            display: flex;
            align-items: flex-end;
            gap: 0.75rem;
            .price {
              @include main-font;
              font-size: $font-size-20;

              .label {
                font-size: $font-size-10;
              }
              &.discount {
                color: $danger;
              }
            }

            .compare {
              display: flex;
              align-items: center;
              font-size: $font-size-15;
              font-weight: 600;
              text-decoration: line-through;

              .label {
                font-size: $font-size-9;
              }
            }
          }
        }

        .color_with_size {
          display: flex;
          flex-direction: column;
          gap: 1.125rem;

          .size_container {
            display: flex;
            flex-direction: column;
            gap: 0.875rem;

            .label {
              display: flex;
              justify-content: space-between;

              a,
              p {
                font-size: $font-size-12;
              }

              .left {
                display: flex;
                gap: 0.5rem;
              }
            }
          }
        }

        .description {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;

          p {
            font-size: $font-size-12;
          }
        }
      }
    }
  }

  .shop_info {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;

    .title {
      font-size: $font-size-15;
      color: $primary;
    }

    .accordion {
      box-sizing: border-box;
      padding: 1.25rem 0;
      border-bottom: 1px solid $primary-20;

      &:first-child {
        border-top: 1px solid $primary-20;
      }

      .content {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-top: 1rem;
        .row {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          flex-wrap: wrap;
          @include md {
            flex-wrap: nowrap;
          }
          p {
            width: 100%;
            font-size: $font-size-14;
            @include md {
              width: auto;
            }
          }
          .store {
            font-size: $font-size-14;
            width: 100%;
            @include md {
              width: 200px;
            }
          }
          .normal {
            color: $primary;
          }
          .few {
            color: $warning;
          }
          .none {
            color: $danger;
          }
        }
      }
    }
  }
}

.stock_mark {
  height: 12px;
  width: 12px;

  img {
    object-fit: cover;
  }
}

.link {
  text-decoration: underline;
}
</style>
